import { useCallback, useEffect, useMemo, useState } from "react"

import appStore from "@/stores/appStore"
import type { Category } from "@/types/product"

import { flattenCategories, pickInitialCategory } from "./catalogMenuModel"

type LoadStatus = "idle" | "loading" | "error"

type UseCatalogMenuStateOptions = {
  initialActiveCategory?: "first" | "none"
  resetActiveCategoryOnClose?: boolean
}

export const useCatalogMenuState = (
  categories: Category[],
  {
    initialActiveCategory = "first",
    resetActiveCategoryOnClose = false,
  }: UseCatalogMenuStateOptions = {}
) => {
  const [open, setOpen] = useState(false)
  const [activeCategory, setActiveCategory] = useState<Category | null>(null)
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("idle")

  const availableCategoryKeys = useMemo(
    () => new Set(flattenCategories(categories).map((category) => category.ref_key)),
    [categories]
  )

  const loadCategories = useCallback(async () => {
    setLoadStatus("loading")

    try {
      await appStore.getCategories()
      setLoadStatus("idle")
    } catch {
      setLoadStatus("error")
    }
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }

    if (initialActiveCategory === "first") {
      setActiveCategory((currentCategory) => {
        if (currentCategory && availableCategoryKeys.has(currentCategory.ref_key)) {
          return currentCategory
        }

        return pickInitialCategory(categories)
      })

      return
    }

    setActiveCategory((currentCategory) => {
      if (!currentCategory || availableCategoryKeys.has(currentCategory.ref_key)) {
        return currentCategory
      }

      return null
    })
  }, [availableCategoryKeys, categories, initialActiveCategory, open])

  useEffect(() => {
    if (open && categories.length === 0 && loadStatus === "idle") {
      void loadCategories()
    }
  }, [categories.length, loadCategories, loadStatus, open])

  useEffect(() => {
    if (categories.length > 0 && loadStatus !== "idle") {
      setLoadStatus("idle")
    }
  }, [categories.length, loadStatus])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)

    if (nextOpen) {
      setActiveCategory(initialActiveCategory === "first" ? pickInitialCategory(categories) : null)
    } else if (resetActiveCategoryOnClose) {
      setActiveCategory(null)
    }
  }

  const closeMenu = () => {
    setOpen(false)

    if (resetActiveCategoryOnClose) {
      setActiveCategory(null)
    }
  }

  return {
    activeCategory,
    closeMenu,
    handleOpenChange,
    isError: loadStatus === "error" && categories.length === 0,
    isLoading: loadStatus === "loading" && categories.length === 0,
    loadCategories,
    open,
    setActiveCategory,
    setOpen,
  }
}
